import React, { useMemo } from 'react';
import type { Test } from '@mariana/labos-client';
import { WorksheetViewer } from '../WorksheetViewer';
import { SpreadsheetTable } from '../SpreadsheetTable';
import { formatAnalyticalValue } from '../../utils/formatValue';
import './ResultViewers.css';

interface LoiResultViewerProps {
  test: Test;
}

const LOI_PARAMS: { key: string; label: string; isText?: boolean }[] = [
  { key: 'report_export_loi', label: 'Loss on Ignition' },
  { key: 'report_export_temperature', label: 'Ignition Temperature' },
  // The analysis time is a datetime string; the analytical formatter would
  // parseFloat it into nonsense, so it is rendered as-is.
  { key: 'report_export_analysis_time', label: 'Analysis Time', isText: true },
];

export const LoiResultViewer: React.FC<LoiResultViewerProps> = ({ test }) => {
  const worksheetData = test.attributes?.worksheet_data as Record<string, any> | undefined;
  const rawWorksheetData = test.attributes?.raw_worksheet_data as Record<string, Record<string, any>> | undefined;

  const reportedRows = useMemo(() => {
    if (!worksheetData) return [];
    return LOI_PARAMS.filter(({ key }) => worksheetData[key] != null).map(({ key, label, isText }) => {
      const entry = worksheetData[key];
      const value = entry?.value ?? entry;
      const unit = (entry?.unit as string | undefined) ?? '';
      const display = isText ? String(value ?? '') : formatAnalyticalValue(value);
      return [label, display, unit] as [string, string, string];
    });
  }, [worksheetData]);

  return (
    <div className="rv-container">
      {rawWorksheetData && (
        <div className="rv-section rv-section-raw">
          <h3 className="rv-section-title">Raw Worksheet</h3>
          <WorksheetViewer rawWorksheetData={rawWorksheetData} />
        </div>
      )}

      {worksheetData && reportedRows.length > 0 && (
        <div className="rv-section rv-section-reported rv-section-reported-wide">
          <h3 className="rv-section-title">Reported Values</h3>
          <SpreadsheetTable
            headers={['Parameter', 'Value', 'Unit']}
            rows={reportedRows}
            showRowNumbers={false}
          />
        </div>
      )}

      {!worksheetData && !rawWorksheetData && (
        <div className="rv-empty">No Loss on Ignition results available for this test.</div>
      )}
    </div>
  );
};
